import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';

import PrimaryButton from '../../shared/button/PrimaryButton';
import { CaptionText, Subheading } from '../../shared/typography/PrimaryTypography';
import {
  approveRecordsBulk,
  fetchRecordSeries,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import { ProblemDetail, RecordSeries } from '../../../types/syntiro_honey';
import { CONFIDENCE_BAND_COLOR, deriveConfidenceBand } from './confidence_band';
import RecordStatusChip from './RecordStatusChip';

interface MetricStoryProps {
  /** The mapped metric id (UUID) whose story to render. */
  metricId: string;
  /** Called after a successful bulk approve so the parent can refresh its list/stats. */
  onApproved: () => void;
}

/** Minimum numeric points needed before a trend line is worth drawing. */
const MIN_POINTS_FOR_CHART = 2;

/** The most specific entity name a series attaches to, for the section heading. */
function seriesEntityLabel(series: RecordSeries): string {
  return series.siteName ?? series.subdivisionName ?? series.divisionName ?? 'Unattributed';
}

/** A point's display value: normalised value preferred, else the raw token, else a dash. */
function pointDisplayValue(value: string | null, rawValue: string | null): string {
  return value ?? rawValue ?? '—';
}

interface SeriesSectionProps {
  /** The metric × entity series to render. */
  series: RecordSeries;
  /** Whether a bulk action is in flight (disables the approve button). */
  busy: boolean;
  /** Approve every not-yet-approved point in this series. */
  onApproveAll: (series: RecordSeries) => void;
}

/** One metric × entity story: a trend chart, a per-period table, and approve-all-shown. */
function SeriesSection({ series, busy, onApproveAll }: SeriesSectionProps) {
  const numericPoints = series.points.filter((point) => point.value !== null);
  const showChart = numericPoints.length >= MIN_POINTS_FOR_CHART;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Subheading weight="Bold">{seriesEntityLabel(series)}</Subheading>
        {series.canonicalUnit !== null && (
          <Chip label={series.canonicalUnit} size="small" variant="outlined" />
        )}
      </Stack>
      {showChart && (
        <LineChart
          height={180}
          xAxis={[
            { scaleType: 'point', data: series.points.map((point) => point.periodLabel ?? '—') }
          ]}
          series={[
            {
              // null values leave a gap rather than a misleading straight line.
              data: series.points.map((point) =>
                point.value === null ? null : Number(point.value)
              ),
              connectNulls: false,
              label: series.canonicalUnit ?? undefined
            }
          ]}
          margin={{ left: 64, right: 16, top: 16, bottom: 24 }}
        />
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Period</TableCell>
            <TableCell align="right">Value</TableCell>
            <TableCell>Confidence</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {series.points.map((point) => (
            <TableRow key={point.recordId}>
              <TableCell>{point.periodLabel ?? 'Unresolved'}</TableCell>
              <TableCell align="right">
                {pointDisplayValue(point.value, point.rawValue)}
                {point.canonicalUnit !== null && ` ${point.canonicalUnit}`}
              </TableCell>
              <TableCell>
                {point.overallConfidence === null ? (
                  <CaptionText>—</CaptionText>
                ) : (
                  <Chip
                    label={deriveConfidenceBand(point.overallConfidence)}
                    size="small"
                    color={CONFIDENCE_BAND_COLOR[deriveConfidenceBand(point.overallConfidence)]}
                  />
                )}
              </TableCell>
              <TableCell>
                <RecordStatusChip status={point.status} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Stack direction="row" justifyContent="flex-end">
        <Tooltip title={series.approvableCount === 0 ? 'Nothing left to approve' : ''}>
          <span>
            <PrimaryButton
              palette_color="success"
              disabled={busy || series.approvableCount === 0}
              loading={busy}
              onClick={() => onApproveAll(series)}
            >
              Approve all shown ({series.approvableCount})
            </PrimaryButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

/**
 * A metric's "story over time": one section per entity, each with a trend chart, a
 * per-period table (value, confidence band, status) and an approve-all-shown
 * action for bulk approval. Approval reuses the bulk-approve endpoint; the server
 * still rejects any ineligible record. Rendered both standalone in {@link StoryDrawer}
 * and as the Story tab of the record detail modal.
 * @returns {JSX.Element}
 */
export default function MetricStory({ metricId, onApproved }: MetricStoryProps) {
  const [series, setSeries] = useState<Array<RecordSeries> | null>(null);
  const [loadProblem, setLoadProblem] = useState<ProblemDetail | null>(null);
  const [actionProblem, setActionProblem] = useState<ProblemDetail | null>(null);
  const [actionBusy, setActionBusy] = useState<boolean>(false);
  const [skippedCount, setSkippedCount] = useState<number | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    try {
      const result = await fetchRecordSeries({ metricId });
      setSeries(result.series);
      setLoadProblem(null);
    } catch (error: unknown) {
      setLoadProblem(toProblemDetail(error));
    }
  }, [metricId]);

  useEffect(() => {
    setSeries(null);
    setLoadProblem(null);
    setActionProblem(null);
    setSkippedCount(null);
    void reload();
  }, [reload]);

  const handleApproveAll = async (target: RecordSeries): Promise<void> => {
    const ids = target.points
      .filter((point) => point.status !== 'APPROVED')
      .map((point) => point.recordId);
    if (ids.length === 0) {
      return;
    }
    setActionBusy(true);
    setActionProblem(null);
    setSkippedCount(null);
    try {
      const result = await approveRecordsBulk(ids);
      setSkippedCount(result.skipped.length);
      await reload();
      onApproved();
    } catch (error: unknown) {
      setActionProblem(toProblemDetail(error));
    } finally {
      setActionBusy(false);
    }
  };

  if (loadProblem !== null) {
    return (
      <Alert severity="error">
        {loadProblem.detail ?? loadProblem.title ?? 'Could not load the story.'}
      </Alert>
    );
  }
  if (series === null) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (series.length === 0) {
    return <Alert severity="info">No records for this metric yet.</Alert>;
  }

  return (
    <Stack spacing={3} divider={<Divider />}>
      {actionProblem !== null && (
        <Alert severity="error" onClose={() => setActionProblem(null)}>
          {actionProblem.detail ?? actionProblem.title ?? 'Approval failed.'}
        </Alert>
      )}
      {skippedCount !== null && (
        <Alert
          severity={skippedCount > 0 ? 'warning' : 'success'}
          onClose={() => setSkippedCount(null)}
        >
          {skippedCount > 0
            ? `Approved the eligible records; ${skippedCount} skipped.`
            : 'Records approved.'}
        </Alert>
      )}
      {series.map((item) => (
        <SeriesSection
          key={`${item.metricId}|${item.divisionId ?? ''}|${item.subdivisionId ?? ''}|${item.siteId ?? ''}`}
          series={item}
          busy={actionBusy}
          onApproveAll={(target) => void handleApproveAll(target)}
        />
      ))}
    </Stack>
  );
}
